import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import './MyEvents.css';

interface Event {
  id: string;
  name: string;
  description: string;
  city: string;
  scheduledDate: string;
  status: string;
  finalGatheringLocation: {
    name: string;
    address: string;
  };
  pairId: string;
  registeredAt: string;
}

const MyEvents: React.FC = () => {
  const { accessToken } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    fetchProfile();
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/profile`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setUser(response.data);
    } catch (err: any) {
      console.error('Error fetching profile:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/my-events`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        timeout: 10000
      });

      const eventsData = response.data.events || [];
      
      // Sort by scheduled date (closest first)
      const sortedEvents = eventsData.sort((a: Event, b: Event) => {
        return new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime();
      });
      
      setEvents(sortedEvents);
    } catch (err: any) {
      setError('Kunne ikke laste arrangementer');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: { [key: string]: { label: string; className: string } } = {
      registration: { label: 'Påmelding åpen', className: 'badge-info' },
      confirmed: { label: 'Bekreftet', className: 'badge-success' },
      active: { label: 'Pågår', className: 'badge-success' },
      completed: { label: 'Fullført', className: 'badge-secondary' },
      cancelled: { label: 'Avlyst', className: 'badge-danger' },
    };

    const statusInfo = statusMap[status] || { label: status, className: 'badge-secondary' };

    return <span className={`badge ${statusInfo.className}`}>{statusInfo.label}</span>;
  };

  if (loading) {
    return (
      <div className="my-events-page">
        <div className="loading">Laster arrangementer...</div>
      </div>
    );
  }

  return (
    <div className="my-events-page">
      <div className="my-events-container">
        <div className="page-header">
          <h1>Mine Sykkelfester</h1>
          <Link to="/bli-med" className="btn btn-primary">
            + Bli med på fest
          </Link>
        </div>

        {!user?.emailVerified && (
          <div className="verification-notice">
            <span className="warning-icon">⚠️</span>
            <div>
              <strong>E-postadressen din er ikke verifisert</strong>
              <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                Du vil ikke motta e-postvarsler om arrangementer før du verifiserer e-postadressen din. 
                Du kan fortsatt se all informasjon i LOGG-fanen for dine arrangementer.
              </p>
            </div>
            <Link to="/deltaker/profil" className="btn btn-primary verify-button">
              Gå til Min profil
            </Link>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {events.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <img src="/logo.png" alt="Sykkelfest logo" style={{ maxWidth: '200px', width: '100%', height: 'auto' }} />
            </div>
            <h2>Ingen arrangementer ennå</h2>
            <p>Du har ikke meldt deg på noen sykkelfester ennå.</p>
            <Link to="/bli-med" className="btn btn-primary">
              Bli med på din første fest!
            </Link>
          </div>
        ) : (
          <div className="events-grid">
            {events.map((event) => {
              const eventDate = new Date(event.scheduledDate);
              const isUpcoming = eventDate > new Date();

              return (
                <div key={event.id} className={`event-card ${!isUpcoming ? 'past-event' : ''}`}>
                  <div className="event-card-header">
                    <h3>{event.name}</h3>
                    {getStatusBadge(event.status)}
                  </div>

                  <div className="event-card-body">
                    <div className="event-info">
                      <span className="info-label">Dato og tid</span>
                      <span className="info-value">
                        <span className="info-icon">📅</span>
                        {eventDate.toLocaleDateString('nb-NO', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                        {' '}kl. {eventDate.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>

                    <div className="event-info">
                      <span className="info-label">Område</span>
                      <span className="info-value">
                        <span className="info-icon">📍</span>
                        {event.city}
                      </span>
                    </div>

                    <div className="event-info">
                      <span className="info-label">Sluttdestinasjon</span>
                      <span className="info-value">
                        <span className="info-icon">🎯</span>
                        {event.finalGatheringLocation.name}
                      </span>
                    </div>
                  </div>

                  {event.description && (
                    <div className="event-description">
                      {event.description}
                    </div>
                  )}

                  <div className="event-card-footer">
                    <Link to={`/deltaker/arrangement/${event.id}`} className="btn btn-primary btn-small">
                      Se detaljer
                    </Link>
                    {isUpcoming && event.status === 'active' && (
                      <Link to={`/deltaker/neste-stopp`} className="btn btn-primary btn-small">
                        Neste stopp
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyEvents;
